import React, { useState, useEffect, useReducer, useCallback, useContext } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";
import InfoIcon from "@material-ui/icons/Info";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import FileModal from "../../components/FileModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { AuthContext } from "../../context/Auth/AuthContext";

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
const reducer = (state, action) => {
    if (action.type === "LOAD_FILES") {
        const files = action.payload;
        const newFiles = [];
        files.forEach((file) => {
            const idx = state.findIndex((s) => s.id === file.id);
            if (idx !== -1) state[idx] = file;
            else newFiles.push(file);
        });
        return [...state, ...newFiles];
    }
    if (action.type === "UPDATE_FILES") {
        const file = action.payload;
        const idx = state.findIndex((s) => s.id === file.id);
        if (idx !== -1) { state[idx] = file; return [...state]; }
        return [file, ...state];
    }
    if (action.type === "DELETE_FILE") {
        const id = action.payload;
        const idx = state.findIndex((s) => s.id === id);
        if (idx !== -1) state.splice(idx, 1);
        return [...state];
    }
    if (action.type === "RESET") return [];
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    nameText: {
        fontWeight: 500,
        fontSize: "0.875rem",
        color: theme.palette.text.primary,
    },
    emptyState: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: theme.spacing(6, 2),
        color: theme.palette.text.disabled,
    },
    emptyIcon: {
        fontSize: 56,
        marginBottom: theme.spacing(1.5),
        opacity: 0.35,
    },
}));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const FileLists = () => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [selectedFileList, setSelectedFileList] = useState(null);
    const [deletingFileList, setDeletingFileList] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [searchParam, setSearchParam] = useState("");
    const [files, dispatch] = useReducer(reducer, []);
    const [fileListModalOpen, setFileListModalOpen] = useState(false);

    const fetchFileLists = useCallback(async () => {
        try {
            const { data } = await api.get("/files/", {
                params: { searchParam, pageNumber },
            });
            dispatch({ type: "LOAD_FILES", payload: data.files });
            setHasMore(data.hasMore);
            setLoading(false);
        } catch (err) {
            toastError(err);
        }
    }, [searchParam, pageNumber]);

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [searchParam]);

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            fetchFileLists();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchParam, pageNumber, fetchFileLists]);

    useEffect(() => {
        const socket = socketConnection({ companyId: user.companyId });

        socket.on(`company-${user.companyId}-file`, (data) => {
            if (data.action === "update" || data.action === "create") {
                dispatch({ type: "UPDATE_FILES", payload: data.files });
            }
            if (data.action === "delete") {
                dispatch({ type: "DELETE_FILE", payload: +data.fileId });
            }
        });

        return () => socket.disconnect();
    }, [user]);

    const handleOpenFileListModal = () => {
        setSelectedFileList(null);
        setFileListModalOpen(true);
    };

    const handleCloseFileListModal = () => {
        setSelectedFileList(null);
        setFileListModalOpen(false);
    };

    const handleSearch = (event) => {
        setSearchParam(event.target.value.toLowerCase());
    };

    const handleEditFileList = (fileList) => {
        setSelectedFileList(fileList);
        setFileListModalOpen(true);
    };

    const handleDeleteFileList = async (fileListId) => {
        try {
            await api.delete(`/files/${fileListId}`);
            toast.success(i18n.t("files.toasts.deleted"));
        } catch (err) {
            toastError(err);
        }
        setDeletingFileList(null);
        setSearchParam("");
        setPageNumber(1);
        dispatch({ type: "RESET" });
    };

    const loadMore = () => setPageNumber((prev) => prev + 1);

    const handleScroll = (e) => {
        if (!hasMore || loading) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + 100) < clientHeight) loadMore();
    };

    return (
        <MainContainer>
            <ConfirmationModal
                title={deletingFileList && i18n.t("files.confirmationModal.deleteTitle")}
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={() => handleDeleteFileList(deletingFileList.id)}
            >
                {i18n.t("files.confirmationModal.deleteMessage")}
            </ConfirmationModal>
            <FileModal
                open={fileListModalOpen}
                onClose={handleCloseFileListModal}
                reload={fetchFileLists}
                aria-labelledby="form-dialog-title"
                fileListId={selectedFileList && selectedFileList.id}
            />
            <MainHeader>
                <Title>
                    {i18n.t("files.title")} ({files.length})
                </Title>
                <Tooltip title={i18n.t("files.infoText")} placement="right" arrow>
                    <IconButton size="small" style={{ marginLeft: 4 }}>
                        <InfoIcon fontSize="small" style={{ opacity: 0.5 }} />
                    </IconButton>
                </Tooltip>
                <MainHeaderButtonsWrapper>
                    <TextField
                        placeholder="Buscar arquivo..."
                        type="search"
                        value={searchParam}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon style={{ color: "gray" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenFileListModal}
                    >
                        {i18n.t("files.buttons.add")}
                    </Button>
                </MainHeaderButtonsWrapper>
            </MainHeader>
            <Paper
                className={classes.mainPaper}
                variant="outlined"
                onScroll={handleScroll}
            >
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell align="left">{i18n.t("files.table.name")}</TableCell>
                            <TableCell align="center">{i18n.t("files.table.actions")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <>
                            {!loading && files.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={2}>
                                        <Box className={classes.emptyState}>
                                            <FolderOpenIcon className={classes.emptyIcon} />
                                            <Typography variant="body2" gutterBottom>
                                                {searchParam
                                                    ? `Nenhum arquivo encontrado para "${searchParam}"`
                                                    : "Nenhuma lista de arquivos cadastrada ainda."}
                                            </Typography>
                                            {!searchParam && (
                                                <Button
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    onClick={handleOpenFileListModal}
                                                    style={{ marginTop: 8 }}
                                                >
                                                    Adicionar primeira lista
                                                </Button>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}

                            {files.map((fileList) => (
                                <TableRow key={fileList.id} hover>
                                    <TableCell align="left">
                                        <Typography className={classes.nameText}>
                                            {fileList.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Editar">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditFileList(fileList)}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Excluir">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setConfirmModalOpen(true);
                                                    setDeletingFileList(fileList);
                                                }}
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && <TableRowSkeleton columns={2} />}
                        </>
                    </TableBody>
                </Table>
            </Paper>
        </MainContainer>
    );
};

export default FileLists;
